import { User } from './Auth.types';
import {
  SetupMarker,
  clearSetupMarker,
  endSetupSubmission,
  getSetupMarker,
  startSetupSubmission,
  writeSetupMarker,
} from './accountSetupStorage';

// 0 roles at the read before the request is recovery mode; 1 or more roles is
// additional-account mode. The mode is fixed for the whole submission.
export type CompleteSignUpMode = 'recovery' | 'additional-account';

export type CompleteSignUpOutcome =
  // The account read before the request failed. Nothing was sent.
  | { kind: 'account-not-loaded' }
  // An unresolved marker exists for the submission's user.
  | { kind: 'unresolved'; marker: SetupMarker }
  | { kind: 'confirmed'; mode: 'recovery'; username: string; activeRoleId: string | null }
  | { kind: 'confirmed'; mode: 'additional-account' }
  // Validation (406) rejected the request. No completion write ran.
  | { kind: 'rejected'; error: unknown }
  // Additional-account mode only: any result that does not confirm completion.
  | { kind: 'failed'; error: unknown }
  // The session changed while the request ran. The caller must not redirect or
  // change account state.
  | { kind: 'session-changed' };

export interface CompleteSignUpDeps {
  // Reads /v2/me for the current session. Throws on failure.
  readMe: () => Promise<User>;
  // Sends POST /v2/complete-sign-up. Throws on an HTTP error.
  completeSignUp: () => Promise<unknown>;
  getSessionToken: () => string | null;
  now: () => Date;
}

// One completion at a time in this page, from the read before the request to
// the end of the result handling, so a second submission (for example with the
// Enter key) sends nothing. The guard belongs to the session that took it, so it
// never blocks a later session, and only the attempt that took it releases it.
let completionGuard: { sessionToken: string | null; attemptId: number } | null = null;
let lastAttemptId = 0;

// Returns the new attempt's ID, or null while another attempt of this session
// holds the guard.
export const takeCompletionGuard = (sessionToken: string | null): number | null => {
  if (completionGuard && completionGuard.sessionToken === sessionToken) {
    return null;
  }
  lastAttemptId += 1;
  completionGuard = { sessionToken, attemptId: lastAttemptId };
  return lastAttemptId;
};

export const releaseCompletionGuard = (attemptId: number) => {
  if (completionGuard?.attemptId === attemptId) {
    completionGuard = null;
  }
};

// Only tests call this. A page load starts with no guard.
export const resetCompletionGuard = () => {
  completionGuard = null;
};

const isCompleted = (body: unknown) =>
  typeof body === 'object' && body !== null && (body as { completed?: unknown }).completed === true;

const getStatus = (error: unknown) =>
  typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;

export const submitCompleteSignUp = async (accountType: string, deps: CompleteSignUpDeps): Promise<CompleteSignUpOutcome> => {
  const sessionToken = deps.getSessionToken();
  const sessionUnchanged = () => deps.getSessionToken() === sessionToken;

  let me: User | null = null;
  try {
    me = await deps.readMe();
  } catch {
    me = null;
  }
  // The request sends the token that is current when it starts. Stop before any
  // marker change or request if another session started during the read.
  if (!sessionUnchanged()) {
    return { kind: 'session-changed' };
  }
  if (!me || typeof me.username !== 'string' || me.username === '' || !Array.isArray(me.roles)) {
    return { kind: 'account-not-loaded' };
  }

  // Every marker write and clear below uses this username, even if another
  // user signs in while the request runs.
  const username = me.username;
  const mode: CompleteSignUpMode = me.roles.length === 0 ? 'recovery' : 'additional-account';

  // An unresolved marker blocks the submission in both modes.
  const existingMarker = getSetupMarker(username);
  if (existingMarker) {
    return { kind: 'unresolved', marker: existingMarker };
  }

  let marker: SetupMarker | null = null;
  if (mode === 'recovery') {
    marker = { username, accountType, submittedAt: deps.now().toISOString() };
    writeSetupMarker(marker);
    startSetupSubmission(username);
  }

  let body: unknown;
  let error: unknown;
  let requestFailed = false;
  try {
    body = await deps.completeSignUp();
  } catch (e) {
    error = e;
    requestFailed = true;
  }
  const confirmed = !requestFailed && isCompleted(body);
  const rejected = requestFailed && getStatus(error) === 406;

  try {
    // Only a confirmed result or a validation rejection resolves the marker.
    // Every other result keeps it.
    if (marker && (confirmed || rejected)) {
      clearSetupMarker(username);
    }
  } finally {
    // From here a kept marker is an unresolved result, and the app shows it.
    if (marker) {
      endSetupSubmission(username);
    }
  }

  if (!sessionUnchanged()) {
    return { kind: 'session-changed' };
  }

  if (confirmed && mode === 'additional-account') {
    return { kind: 'confirmed', mode };
  }
  if (confirmed) {
    let refreshed: User | null = null;
    try {
      refreshed = await deps.readMe();
    } catch {
      refreshed = null;
    }
    if (!sessionUnchanged() || (refreshed && refreshed.username !== username)) {
      return { kind: 'session-changed' };
    }
    return { kind: 'confirmed', mode, username, activeRoleId: refreshed?.activeRoleId ?? null };
  }
  if (rejected) {
    return { kind: 'rejected', error };
  }
  if (marker) {
    return { kind: 'unresolved', marker };
  }
  return { kind: 'failed', error };
};
